import { PlanColumn, PlanPeriod } from "modules/spending/workload-plan/models/plan-types";

/** Вид участника проекта — совпадает с ProjectPlanRowKinds на бэке */
export type ProjectRowKind = "employer" | "outsourcer";

/** Отрезок работ в координатах колонок сетки */
export interface PlanSegment {
    /** Индекс первой видимой колонки */
    start: number;
    /** Видимая длина в колонках — сколько отрезка попало на экран */
    length: number;
    /**
     * Настоящая длина отрезка в рабочих днях, до обрезки по диапазону.
     * Именно её показываем: видимая длина — это не срок работ, а размер окна.
     */
    workDays: number;
    /** Настоящий последний день отрезка, "2028-12-15"; может лежать за краем сетки */
    stopDay: string;
    /** Отрезок начался левее видимой сетки */
    continuesLeft: boolean;
    /** Отрезок продолжается правее видимой сетки */
    continuesRight: boolean;
}

/** Строка графика: сотрудник или договор с подрядчиком */
export interface ProjectParticipant {
    kind: ProjectRowKind;
    id: string;
    /** ФИО сотрудника или наименование подрядчика */
    label: string;
    /** Должность сотрудника или номер договора подрядчика */
    note: string;
    segments: PlanSegment[];
}

/**
 * Оформление проекта. Считается один раз при сборке модели: цвет у всех полос
 * проекта общий, гонять hex2rgba/shadeColor на каждый рендер незачем.
 *
 * Яркостей две, и путать их нельзя. Полоса полупрозрачная и лежит на белой сетке,
 * поэтому её контраст считается по цвету, УЖЕ смешанному с белым. Плашка с номером
 * залита чистым цветом проекта — у неё своя яркость и свой цвет текста.
 */
export interface ProjectPaint {
    /** Заливка полосы — цвет проекта, смешанный с белым */
    background: string;
    /** Рамка полосы */
    borderColor: string;
    /** Заливка полосы тёмная — подпись белая, иначе тёмно-синяя */
    dark: boolean;
    /** Чистый цвет проекта — заливка плашки с номером */
    chipBackground: string;
    /** Плашка тёмная — номер белый, иначе тёмно-синий */
    chipDark: boolean;
}

export interface ProjectPlanProject {
    id: string;
    name: string;
    color: string;
    inventoryNumber: number;
    fullInventoryNumber: string;
    orderNumber: string;
    status: string;
    statusColor: string;
    /** Главный инженер проекта, «Фамилия И. О.» */
    employer: string;
    /** Крайний день работ по проекту, "2026-09-11"; считается бэком по всем отрезкам */
    lastDay: string;
    paint: ProjectPaint;
    rows: ProjectParticipant[];
    /**
     * Сводная полоса — от самой ранней работы проекта до самой поздней.
     * Считается на сервере по ВСЕМ участникам, до фильтрации по отделу, и не
     * пересчитывается по видимым строкам: сколько бы их ни осталось после прав
     * и поиска, длина проекта показывается целиком.
     */
    summary: PlanSegment | null;
}

export interface ProjectPlanSnapshot {
    columns: PlanColumn[];
    periods: PlanPeriod[];
    projects: ProjectPlanProject[];
}

export const EMPTY_PROJECT_PLAN: ProjectPlanSnapshot = { columns: [], periods: [], projects: [] };

export type { PlanColumn, PlanPeriod };
